import React from 'react';
import { Link } from 'react-router-dom';
import { ClipboardListIcon, FolderOpenIcon, LineChartIcon, ScaleIcon, TableIcon } from 'lucide-react';
import { useAssessment, useInstrumentConfig } from '../contexts/AssessmentContext';
import { LedgerTable } from '../components/workspace/LedgerTable';
import { BudgetTable } from '../components/workspace/BudgetTable';
import { BundleTable } from '../components/workspace/BundleTable';
import { InstructionsPanel } from '../components/workspace/InstructionsPanel';
import { DocumentViewer } from '../components/workspace/DocumentViewer';
import { AIChat } from '../components/workspace/AIChat';
import { ReflectionPanel } from '../components/workspace/ReflectionPanel';
import { TabId } from '../types/assessment';
const TAB_ICONS: Record<TabId, typeof TableIcon> = {
  reconciliation: TableIcon,
  budget: LineChartIcon,
  documents: FolderOpenIcon,
  bundle: ScaleIcon,
  instructions: ClipboardListIcon
};
export function Workspace() {
  const {
    startedAt,
    activeTab,
    setActiveTab,
    timeUp,
    submitted
  } = useAssessment();
  const config = useInstrumentConfig();
  if (!startedAt) {
    return <main className="flex w-full flex-1 items-center justify-center bg-slate-100 p-6" data-id="element-895" data-mp="2d8qc9dpzod8q">
        <div className="max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center" data-id="element-896" data-mp="2rxe1z3d5lsqr">
          <h1 className="text-lg font-semibold text-slate-900" data-id="element-897" data-mp="2n3i9ggfnsy2z">The assessment hasn&apos;t started</h1>
          <p className="mt-2 text-sm text-slate-600" data-id="element-898" data-mp="30uwu0rp7t7c5">
            Read the briefing and start the clock before entering the workspace.
          </p>
          <Link to="/briefing" className="mt-5 inline-block rounded-md bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-700" data-id="element-899" data-mp="3auf8wtq9q2xc">
            Go to briefing
          </Link>
        </div>
      </main>;
  }
  return <main className="w-full flex-1 bg-slate-100" data-id="element-900" data-mp="1bhjmktlt336t">
      {(timeUp || submitted) && <div className="border-b border-slate-800 bg-slate-900 px-6 py-2.5 text-center text-sm text-slate-200" data-id="element-901" data-mp="1bjwcpu1pwjuy">
          {submitted ? 'Submitted — this workspace is now read-only.' : 'Time is up. The workspace is read-only; complete your reflection below and submit.'}
        </div>}

      <div className="mx-auto max-w-[1600px] px-6 py-6" data-id="element-902" data-mp="3pjc355lwm8zy">
        <div className="grid min-h-0 gap-5 lg:grid-cols-[minmax(0,1fr)_400px]" data-id="element-903" data-mp="3pkvjsxbh2m29">
          <div className="flex h-[72vh] min-h-[560px] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-904" data-mp="3uhvop4wl5irs">
            <div className="flex items-center gap-1 border-b border-slate-200 bg-slate-50 px-3 pt-2.5" data-id="element-905" data-mp="3a11x2n5hera">
              {config.tabs.map(tab => {
              const active = tab.id === activeTab;
              const Icon = TAB_ICONS[tab.id];
              return <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} aria-current={active ? 'page' : undefined} className={`flex items-center gap-2 rounded-t-md border border-b-0 px-4 py-2 text-[13px] font-medium transition-colors ${active ? 'border-slate-200 bg-white text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'}`} data-id="element-906" data-mp="h5buuahu3pt0">
                    <Icon className="h-3.5 w-3.5" data-id="element-907" data-mp="1ew8k09v8gg81" />
                    {tab.label}
                  </button>;
            })}
            </div>

            <div className="min-h-0 flex-1 overflow-hidden" data-id="element-908" data-mp="157gqhxaixnv0">
              {activeTab === 'reconciliation' && <LedgerTable data-id="element-909" data-mp="115v8xyn1oleh" />}
              {activeTab === 'budget' && <BudgetTable data-id="element-910" data-mp="115dff9rrola9" />}
              {activeTab === 'bundle' && <BundleTable data-id="element-911" data-mp="1xob27pniw4ke" />}
              {activeTab === 'instructions' && <InstructionsPanel data-id="element-912" data-mp="1xpvx5gdr1pis" />}
              {activeTab === 'documents' && <DocumentViewer data-id="element-913" data-mp="1sw9ixjrxtdkv" />}
            </div>
          </div>

          <div className="h-[72vh] min-h-[560px] overflow-hidden rounded-xl border border-slate-200 shadow-sm" data-id="element-914" data-mp="1dei9m9ga5b9z">
            <AIChat data-id="element-915" data-mp="1ieecu6h5kyoh" />
          </div>
        </div>
      </div>

      <ReflectionPanel data-id="element-916" data-mp="1ibrxvlrxngw8" />
    </main>;
}